import React from "react";
import styled from "styled-components";
import CaseStudiesCard from "../CaseStudiesCard";
import Links from "../../../Links";
import Heading from "../../../Heading";
import ImageOne from "../../../../img/casestudy/design-systems/muibook-playground.png";
import Stack from "../../../Stack";
import Box from "../../../Box";
import Rule from "../../../Rule";
import Image from "../../../Image";
import BlogLayout from "../BlogLayout";

const Message = styled.div`
  width: 100%;
  border-radius: 8px;
  padding: var(--spacing-small);
  background: rgba(0 0 0 / 0.2);
  display: flex;
  gap: var(--spacing-x-small);
  p {
    margin: 0;
    font-size: var(--font-size-text-small);
  }
`;

const Muiplay = (
  <CaseStudiesCard id="muiplay">
    <Heading
      variant="smaller"
      style={{ marginTop: 0, marginBottom: "var(--spacing-x-tiny)", textTransform: "uppercase" }}
    >
      ARTICLE
    </Heading>
    <p style={{ marginBottom: 0, fontSize: "var(--font-size-text-medium)" }}>
      Exploring how tooling and AI can use design systems to speed prototyping and ensure consistency.
    </p>
    <Rule spacing="medium" inverted mt="var(--spacing-medium)" mb="var(--spacing-large)" />
    <BlogLayout>
      <Stack spacing="var(--spacing-xx-large)">
        <Box>
          <Heading variant="larger" mb="0">
            Muiplay
          </Heading>

          <p>
            Muiplay lets you describe the layout you want, for example “a sidebar with a header and table”, and it
            instantly builds it using components from Michael UI. It is designed to remove the friction of setup,
            letting you focus on visualising ideas, potential structure, flow, and functionality from the very start.
          </p>
          <Message style={{ marginBottom: "var(--spacing-medium)" }}>
            {" "}
            <span role="img" aria-label="Read more on Medium">
              📖
            </span>
            <Stack disableMargin>
              <Heading variant="smallest" mt="0" mb="0" color="white">
                Read the Full Muibook Article?
              </Heading>
              <p>
                Explore Muiplay and other features that bring a Design System to life.{" "}
                <Links href="#muibook">Read the Article</Links>
              </p>
            </Stack>
          </Message>
          <p style={{ marginBottom: "var(--spacing-large)" }}>
            Whether you are mocking up ideas, testing flows, or starting a real project, Muiplay speeds up the process
            by giving you a clean, extensible layout from a natural-language prompt. It is your shortcut to clean code
            and design consistency.
          </p>
          <Image dark src={ImageOne} alt="Playground" caption="play.muibook.com" />
        </Box>
        <Box>
          {" "}
          <Heading variant="large">Key Benefits</Heading>
          <Heading variant="smaller" mt="var(--spacing-medium)" mb="var(--spacing-tiny)">
            Prototype layouts at the speed of thought
          </Heading>
          <p>
            Skip the boilerplate. With just a prompt, Muiplay generates layout-ready MUI code so you can move from idea
            to structure instantly.
          </p>
          <Heading variant="smaller" mb="var(--spacing-tiny)">
            Structure-first, code-ready
          </Heading>
          <p>
            Focus on flow and usability while Muiplay handles the scaffolding. Use it to wireframe in real code, then
            refine and scale as your project grows.
          </p>
          <Heading variant="smaller" mb="var(--spacing-tiny)">
            Your shortcut to smarter prototyping
          </Heading>
          <p>
            Explore and validate ideas in code with Muiplay, then refine your design in Figma with MuiKit. Together,
            they create a seamless workflow from concept to polished UI.
          </p>
        </Box>
        <Box>
          <Heading variant="large" mb="var(--spacing-small)">
            Component Library
          </Heading>
          <p>
            Muiplay is powered by Michael UI components. The MUI Playground uses the CDN build published to NPM,
            allowing Web Components to load dynamically within the Agent’s sandboxed modal. This provides a lightweight,
            zero-install method for making the design system available in runtime and AI-driven environments.
          </p>
          <p>
            Learn more at{" "}
            <Links href="https://www.muibook.com" target="_blank" rel="noopener noreferrer">
              muibook.com
            </Links>
            .
          </p>
        </Box>
        <Box>
          <Heading variant="large" mb="var(--spacing-small)">
            Technical Highlights
          </Heading>
          <ul>
            <li>Built a MUI Playground that uses AI to explore and interact with design components</li>
            <li>Architected and exported a comprehensive set of keywords and prompts through the NPM package</li>
            <li>
              Empowered AI tools with a deep understanding of the design system to enable smarter, context-aware usage
            </li>
            <li>Launched the live AI Playground (Alpha) at Medium as a proof of concept</li>
            <li>
              Additional defined prompts and broader component coverage are planned, with continued iteration expected
              after publication
            </li>
          </ul>
        </Box>
      </Stack>
    </BlogLayout>
  </CaseStudiesCard>
);

export default Muiplay;
